import { ComponentProps } from 'react';
import type { IOffer } from '@bridebook/models/source/models/Suppliers/Offers.types';
import { serializeTimestamp } from '@bridebook/toolbox/src/serialize-timestamps';
import { type TagThemes } from '@bridebook/aura';
import { Well } from '@bridebook/ui';
import { getI18n } from '@bridebook/web/lib/i18n/getI18n';
import { OfferDiscountTypes, OfferTypes } from '@bridebook/web/lib/offers/constants';
import { getDiscountLabel } from '../utils';

interface TileConfig {
  title: string;
  subtitle: string;
  tag?: string;
  expiration?: number;
  theme: 'indigo' | 'teal' | 'peach';
  wellTheme: ComponentProps<typeof Well>['theme'];
  tagTheme: TagThemes;
  timeLeftTagTheme: TagThemes;
}

export const getTileConfigV2 = (
  type: OfferTypes,
  offer: IOffer,
  currencySymbol: string,
): TileConfig => {
  const { expiration, discount } = offer;
  const i18n = getI18n();

  const defaultConfig: TileConfig = {
    title: offer.title,
    subtitle: typeof discount === 'string' ? discount : getDiscountLabel(offer, currencySymbol),
    theme: 'indigo',
    wellTheme: 'blue',
    tagTheme: 'indigoCrush',
    timeLeftTagTheme: 'indigoCrush10',
    tag: i18n.t('offers:type.default.tag'),
    expiration: serializeTimestamp(expiration),
  };

  switch (type) {
    case OfferTypes.lateAvailability:
      return {
        ...defaultConfig,
        title: i18n.t('offers:type.lateAvailability.title'),
        tag: i18n.t('offers:type.lateAvailability.tag'),
        theme: 'teal',
        tagTheme: 'teal80',
        wellTheme: 'teal',
        timeLeftTagTheme: 'teal80',
      };

    case OfferTypes.midweek:
      return {
        ...defaultConfig,
        title: i18n.t('offers:type.midweek.title'),
      };

    case OfferTypes.seasonal:
      return {
        ...defaultConfig,
        title: i18n.t('offers:type.seasonal.title'),
      };

    case OfferTypes.custom:
      switch (offer.discountType) {
        case OfferDiscountTypes.other:
          return { ...defaultConfig, title: i18n.t('offers:type.custom.title') };

        default:
          return defaultConfig;
      }

    case OfferTypes.bbDiscount:
      return {
        ...defaultConfig,
        title: i18n.t('offers:type.discount.title'),
        tag: i18n.t('offers:type.discount.tag'),
        theme: 'peach',
        tagTheme: 'peachRose120',
        wellTheme: 'peachRose',
        timeLeftTagTheme: 'peachRose60',
      };

    default:
      return defaultConfig;
  }
};
